import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ErrorMessage {
  message: string;
  type: 'error' | 'warning' | 'info' | 'success';
  statusCode?: number;
  timestamp: Date;
  /** When set, notification auto-dismisses after this many milliseconds. */
  autoDismissMs?: number;
}

export interface ShowErrorOptions {
  autoDismissMs?: number;
}

/** Strip internal DB/host details from API errors shown to users. */
export function sanitizeClientErrorMessage(raw: string | null | undefined, fallback = 'An unexpected error occurred'): string {
  const msg = String(raw ?? '').trim();
  if (!msg) {
    return fallback;
  }
  const lower = msg.toLowerCase();
  if (
    lower.includes('i/o timeout') ||
    lower.includes('connection timed out') ||
    lower.includes('context deadline exceeded') ||
    lower.includes('timeout') && /\d{1,3}(\.\d{1,3}){3}:\d+/.test(msg)
  ) {
    return 'Unable to connect to the database. The server did not respond in time. Please verify the database is running and reachable, then try again.';
  }
  if (
    lower.includes('connection refused') ||
    lower.includes('no such host') ||
    lower.includes('dial tcp') ||
    lower.includes('unable to open tcp connection')
  ) {
    return 'Unable to connect to the database. Please verify the backend database configuration and network connection.';
  }
  if (lower.includes('login failed for user') || lower.includes('authentication failed')) {
    return 'Database login failed. Please contact your administrator.';
  }
  return msg;
}

/** Extract a user-safe message from HTTP/GraphQL error payloads. */
export function extractClientSafeApiErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  const err = error as {
    errors?: Array<{ message?: string }>;
    error?: { errors?: Array<{ message?: string }>; message?: string };
    message?: string;
  } | null;

  let raw = fallback;
  if (Array.isArray(err?.errors) && err.errors.length > 0) {
    raw = err.errors.map((e) => e?.message || JSON.stringify(e)).join('; ');
  } else if (Array.isArray(err?.error?.errors) && err.error.errors.length > 0) {
    raw = err.error.errors.map((e) => e?.message || JSON.stringify(e)).join('; ');
  } else if (err?.error?.message) {
    raw = err.error.message;
  } else if (err?.message) {
    raw = err.message;
  }
  return sanitizeClientErrorMessage(raw, fallback);
}

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  private errorSubject = new BehaviorSubject<ErrorMessage | null>(null);
  public readonly error$: Observable<ErrorMessage | null> = this.errorSubject.asObservable();

  showError(message: string, statusCode?: number, options?: ShowErrorOptions): void {
    this.errorSubject.next({
      message: this.getErrorMessage(message, statusCode),
      type: 'error',
      statusCode,
      timestamp: new Date(),
      autoDismissMs: options?.autoDismissMs
    });
  }

  showWarning(message: string): void {
    this.errorSubject.next({
      message,
      type: 'warning',
      timestamp: new Date()
    });
  }

  showInfo(message: string): void {
    this.errorSubject.next({
      message,
      type: 'info',
      timestamp: new Date()
    });
  }

  showSuccess(message: string, options?: { autoDismissMs?: number }): void {
    this.errorSubject.next({
      message,
      type: 'success',
      timestamp: new Date(),
      autoDismissMs: options?.autoDismissMs
    });
  }

  clearError(): void {
    this.errorSubject.next(null);
  }

  private getErrorMessage(message: string, statusCode?: number): string {
    if (!statusCode) {
      return message || 'An unexpected error occurred';
    }

    switch (statusCode) {
      case 400:
        return message || 'Invalid request. Please check your input and try again.';
      
      case 401:
        return 'Your session has expired. Please log in again.';
      
      case 403:
        return 'You do not have permission to perform this action.';
      
      case 404:
        return message || 'The requested resource was not found.';
      
      case 405:
        return 'This action is not allowed.';
      
      case 408:
        return 'The request timed out. Please try again.';
      
      case 409:
        return message || 'A conflict occurred. Please refresh and try again.';
      
      case 422:
        return message || 'The request could not be processed. Please check your input.';
      
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      
      case 500:
        return 'A server error occurred. Please try again later.';
      
      case 502:
        return 'Service temporarily unavailable. Please try again later.';
      
      case 503:
        return 'Service is currently unavailable. Please try again later.';
      
      case 504:
        return 'The service timed out. Please try again later.';
      
      case 402:
      case 406:
      case 407:
      case 410:
      case 411:
      case 412:
      case 413:
      case 414:
      case 415:
      case 416:
      case 417:
      case 418:
      case 421:
      case 423:
      case 424:
      case 425:
      case 426:
      case 428:
      case 431:
      case 451:
        return message || `Client error (${statusCode}). Please check your request.`;
      
      case 501:
      case 505:
      case 506:
      case 507:
      case 508:
      case 510:
      case 511:
        return message || `Server error (${statusCode}). Please try again later.`;
      
      default:
        return message || `An error occurred (${statusCode}). Please try again.`;
    }
  }

  handleHttpError(error: any, statusCode?: number): void {
    let errorMessage = 'An unexpected error occurred';

    if (error?.error?.message) {
      errorMessage = error.error.message;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    const finalStatusCode = statusCode || error?.status || error?.statusCode;
    this.showError(errorMessage, finalStatusCode);
  }
}
